import { Button, formatDate, formatPhone, ListView, PageHeader, RecordDrawer, useCursorList } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Input } from "@majitap/ui/arc/input/input";
import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { paymentTermsQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { useFormCheck } from "../../form-check";
import { APPROVAL_STATUS, type ApprovalStatus } from "../../labels";
import { filterSearch } from "../../list-search";
import { type PartnerFields, partnerActions, payoutState } from "../../partners";
import { useDebounced } from "../../search";
import { DrawerFooter } from "../shared/drawer-footer";
import styles from "./partners.module.css";

const STATUSES = Object.keys(APPROVAL_STATUS) as ApprovalStatus[];
const PAYOUTS = ["none", "waiting", "approved"] as const;
// term: a payment term's id, or "default" for the company's default
export const partnerFilters = filterSearch({ q: "text", status: STATUSES, payout: PAYOUTS, term: "text" });

export const PAYOUT_LABEL = {
  none: "Not set",
  waiting: "Waiting for approval",
  approved: "Approved",
} as const;

/** Partners (P8-13): third-party suppliers, from application to approval. */
export function PartnersPage(): ReactNode {
  const search = useSearch({ from: "/console/partners" });
  const navigate = useNavigate({ from: "/partners" });
  const format = useCompanyFormat();
  const [typed, setTyped] = useState(search.q ?? "");
  const q = useDebounced(typed.trim());
  useEffect(() => {
    if ((search.q ?? "") !== q) void navigate({ search: (s) => ({ ...s, q: q || undefined }), replace: true });
  }, [q, search.q, navigate]);
  const [adding, setAdding] = useState(false);
  const terms = useQuery(paymentTermsQuery);
  const list = useCursorList({
    queryKey: ["partners", search.q ?? "", search.status ?? "", search.payout ?? "", search.term ?? ""],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/partners", {
        params: {
          query: {
            q: search.q,
            approval_status: search.status,
            payout: search.payout,
            payment_term_id: search.term,
            cursor,
            limit: 25,
          },
        },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });

  return (
    <>
      <PageHeader
        title="Partners"
        description="Third-party suppliers: trucking companies and fill-site owners who deliver for you."
        actions={<Button onClick={() => setAdding(true)}>Add partner</Button>}
      />
      <ListView
        caption="Partners"
        itemName={{ one: "partner", other: "partners" }}
        rows={list.rows}
        rowKey={(p) => p.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={partnerFilters.active(search).length > 0}
        onResetFilters={() => {
          setTyped("");
          void navigate({ search: {} });
        }}
        empty={{
          title: "No partners yet",
          description: "Add a trucking company or fill-site owner, then approve them once their papers check out.",
        }}
        toolbar={
          <div className={styles.toolbar}>
            <div className={styles.searchBox}>
              <SearchField
                label="Search partners"
                value={typed}
                onValueChange={setTyped}
                placeholder="Name, phone, email or tax PIN"
              />
            </div>
            <SegmentedControl
              label="Status"
              value={search.status ?? "all"}
              onValueChange={(v) =>
                navigate({ search: (s) => ({ ...s, status: v === "all" ? undefined : (v as ApprovalStatus) }) })
              }
              options={[
                { value: "all", label: "All" },
                ...STATUSES.map((s) => ({ value: s, label: APPROVAL_STATUS[s][0] })),
              ]}
            />
            <Select
              label="Payout details"
              value={search.payout ?? "any"}
              onValueChange={(v) =>
                navigate({
                  search: (s) => ({ ...s, payout: v === "any" ? undefined : (v as (typeof PAYOUTS)[number]) }),
                })
              }
              options={[{ value: "any", label: "Any" }, ...PAYOUTS.map((p) => ({ value: p, label: PAYOUT_LABEL[p] }))]}
            />
            <Select
              label="Payment term"
              value={search.term ?? "any"}
              onValueChange={(v) => navigate({ search: (s) => ({ ...s, term: v === "any" ? undefined : v }) })}
              options={[
                { value: "any", label: "Any" },
                { value: "default", label: "Company default" },
                ...(terms.data ?? []).map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
          </div>
        }
        columns={[
          {
            key: "name",
            label: "Partner",
            render: (p) => (
              <span className={styles.stack}>
                <Link to="/partners/$partnerId" params={{ partnerId: p.id }} className={styles.link}>
                  {p.name}
                </Link>
                <span className={styles.muted}>{formatPhone(p.phone) || p.email || "No contact"}</span>
              </span>
            ),
          },
          { key: "members", label: "Members", render: (p) => String(p.members.length) },
          {
            key: "payout",
            label: "Payout details",
            render: (p) => {
              const state = payoutState(p.payout);
              return <span className={state === "waiting" ? styles.waiting : styles.muted}>{PAYOUT_LABEL[state]}</span>;
            },
          },
          {
            key: "approval_status",
            label: "Status",
            render: (p) => (
              <Badge tone={APPROVAL_STATUS[p.approval_status][1]}>{APPROVAL_STATUS[p.approval_status][0]}</Badge>
            ),
          },
          { key: "created_at", label: "Added", render: (p) => formatDate(p.created_at, format) },
        ]}
      />
      {adding ? <PartnerDialog onClose={() => setAdding(false)} /> : null}
    </>
  );
}

/**
 * Add a partner (it starts pending), or edit one's contact details. A first owner can be added
 * with it: they get an SMS and sign in to the partner portal once the partner is approved.
 */
export function PartnerDialog({
  partner,
  onClose,
}: {
  partner?: {
    id: string;
    name: string;
    tax_pin: string | null;
    email: string | null;
    phone: string | null;
    payment_term_id: string | null;
    etims_invoice_required: boolean;
  };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState(partner?.name ?? "");
  const [phone, setPhone] = useState(partner?.phone ?? "");
  const [email, setEmail] = useState(partner?.email ?? "");
  const [pin, setPin] = useState(partner?.tax_pin ?? "");
  // how often the partner is paid; empty is the company's default terms
  const [termId, setTermId] = useState(partner?.payment_term_id ?? "default");
  const terms = useQuery(paymentTermsQuery);
  const [etims, setEtims] = useState(partner?.etims_invoice_required ?? false);
  const [ownerName, setOwnerName] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const owner =
    ownerName.trim() || ownerPhone.trim() ? { name: ownerName.trim(), phone_number: ownerPhone.trim() } : null;
  const check = useFormCheck();
  // the API's phone rule: E.164
  const isPhone = (v: string) => /^\+[1-9]\d{8,14}$/.test(v.trim());
  const errors = {
    name: !name.trim() ? "Enter the partner's name." : name.trim().length > 120 ? "At most 120 characters." : undefined,
    phone: !phone.trim() || isPhone(phone) ? undefined : "A phone number with its country code, e.g. +254712345678.",
    email: !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? undefined : "Enter an email address.",
    pin: pin.trim().length <= 20 ? undefined : "At most 20 characters.",
    // the owner is optional, but once either field is filled both are needed
    ownerName: !owner
      ? undefined
      : !owner.name
        ? "Enter their name too, or clear their phone."
        : owner.name.length > 120
          ? "At most 120 characters."
          : undefined,
    ownerPhone: !owner
      ? undefined
      : !owner.phone_number
        ? "Enter their phone too, or clear their name."
        : isPhone(owner.phone_number)
          ? undefined
          : "A phone number with its country code, e.g. +254712345678.",
  };
  const mutation = useMutation({
    mutationFn: () => {
      const fields: PartnerFields = {
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        tax_pin: pin.trim() || null,
        payment_term_id: termId === "default" ? null : termId,
        etims_invoice_required: etims,
      };
      return partner
        ? partnerActions.update(partner.id, fields)
        : partnerActions.create({ ...fields, owner: owner ?? undefined });
    },
    onSuccess: (saved) => {
      onClose();
      if (!partner) void navigate({ to: "/partners/$partnerId", params: { partnerId: saved.id } });
    },
  });
  return (
    <RecordDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={partner ? `Edit ${partner.name}` : "Add a partner"}
      description={partner ? undefined : "It starts pending. Approve it once its papers check out."}
    >
      <div className={styles.form}>
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Maji Haulage Ltd"
          error={check.show(errors.name)}
        />
        <div className={styles.pair}>
          <Input
            label="Phone (optional)"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+254712345678"
            error={check.show(errors.phone)}
          />
          <Input
            label="Email (optional)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={check.show(errors.email)}
          />
        </div>
        <Input
          label="Tax PIN (optional)"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          error={check.show(errors.pin)}
        />
        <Select
          label="Payout terms"
          description="The terms agreed with the partner, kept on record. Payouts don't follow them yet."
          value={termId}
          onValueChange={setTermId}
          options={[
            { value: "default", label: "The company's default" },
            ...(terms.data ?? []).filter((t) => t.is_active).map((t) => ({ value: t.id, label: t.name })),
          ]}
        />
        <Switch label="Payout requests need an eTIMS invoice" checked={etims} onCheckedChange={setEtims} />
        <p className={styles.muted}>
          When on, the partner attaches its KRA eTIMS invoice (a PDF) to each payout request, and a request without one
          can't be approved.
        </p>
        {partner ? null : (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>First owner (optional)</legend>
            <p className={styles.muted}>They get an SMS and sign in to the partner portal with this number.</p>
            <div className={styles.pair}>
              <Input
                label="Their name"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                error={check.show(errors.ownerName)}
              />
              <Input
                label="Their phone"
                inputMode="tel"
                value={ownerPhone}
                onChange={(e) => setOwnerPhone(e.target.value)}
                placeholder="+254712345678"
                error={check.show(errors.ownerPhone)}
              />
            </div>
          </fieldset>
        )}
        {mutation.error ? (
          <Alert tone="danger" title="Not saved">
            {messageFor(errorOf(mutation.error))}
          </Alert>
        ) : null}
        <DrawerFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={mutation.isPending} onClick={() => check.submit(errors, () => mutation.mutate())}>
            {partner ? "Save partner" : "Add partner"}
          </Button>
        </DrawerFooter>
      </div>
    </RecordDrawer>
  );
}
